import React, { useState, useEffect } from 'react';
import { QueryClient, QueryClientProvider } from 'react-query';
import { ReactQueryDevtools } from 'react-query/devtools';
import { ErrorBoundary } from 'react-error-boundary';
import { datadogLogs } from '@datadog/browser-logs';
import { BrowserRouter as Router } from 'react-router-dom';
import styled from 'styled-components';

import PrivateRoute from 'components/PrivateRoute';

import Theme from './styles/theme';
import Routes from './utils/routes';
import { useAuth0 } from './utils/auth0';

const LoadingWrapper = styled.div``;

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Turn off some of the default behavior of react-query because we have some heavy queries
      // that we don't want to refetch on window focus. If you need this behavior, opt into it on
      // the instance of UseQuery that you create locally in your component.
      staleTime: Infinity,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchOnMount: false,
      retry: false,
    },
  },
});

function RoutesWithAuth0() {
  const [loadingMessage, setLoadingMessage] = useState('');
  const { loading, isAuthenticated, logout, token, user } = useAuth0();

  useEffect(() => {
    if (token) {
      // do stuff
    } else if (!token) {
      setLoadingMessage('Loading'); // Awaiting Auth0 token...
    }
    // eslint-disable-next-line
  }, [token]);

  if (!isAuthenticated) {
    return <PrivateRoute path="/" exact render={() => <div>{loadingMessage}</div>} />;
  }

  if (loading) {
    return <LoadingWrapper>{loadingMessage}</LoadingWrapper>;
  }

  return <Routes logout={logout} user={user} isPrivate />;
}

if (global.APP.RUNTIME !== 'development') {
  datadogLogs.init({
    clientToken: 'pub26c267ceb8abc166ed08d7537abb4117',
    forwardErrorsToLogs: true,
    sampleRate: 100,
    version: process.env.REACT_APP_GIT_SHA,
    service: 'admin-browser',
  });
}

const onErrorHandler = (error, info) => {
  datadogLogs.logger.info('Error boundary hit', {
    pathname: window?.location?.pathname,
    error,
    info,
  });
};

const App = ({ enableAuth0 }) => {
  return (
    <ErrorBoundary onError={onErrorHandler}>
      <QueryClientProvider client={queryClient}>
        <Router>
          <Theme>{enableAuth0 ? <RoutesWithAuth0 /> : <Routes isPrivate={false} />}</Theme>
        </Router>
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </ErrorBoundary>
  );
};

App.defaultProps = {
  enableAuth0: false,
  history: null,
};

export default App;
